// Custom SVG Icons for TouchBase Landing Page
// Minimal, geometric, forensic-themed design language

import React from 'react';

// Helper for consistent styling
const Icon = ({ children, size = 24, className = '', ...props }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="square"
    strokeLinejoin="miter"
    className={className}
    {...props}
  >
    {children}
  </svg>
);

// Play - Triangle pointing right
export const PlayIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <polygon points="6,4 20,12 6,20" fill="currentColor" stroke="none" />
  </Icon>
);

// Balance/Gauge - Dial meter for measuring balance
export const BalanceIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M4 18 A10 10 0 0 1 20 18" fill="none" />
    <circle cx="12" cy="18" r="2" fill="currentColor" />
    <line x1="12" y1="18" x2="12" y2="8" strokeWidth="2" />
    <line x1="5" y1="14" x2="7" y2="15" />
    <line x1="19" y1="14" x2="17" y2="15" />
  </Icon>
);

// Path/Route - Movement tracking path
export const CursorIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="5" cy="5" r="2" fill="currentColor" />
    <circle cx="19" cy="19" r="2" fill="currentColor" />
    <path d="M5 5 Q12 5 12 12 Q12 19 19 19" fill="none" strokeDasharray="2 2" />
    <circle cx="12" cy="12" r="1.5" fill="currentColor" />
  </Icon>
);

// Quality/Scan - AI scanning grid
export const QualityIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="3" y="3" width="18" height="18" fill="none" />
    <line x1="3" y1="9" x2="21" y2="9" strokeDasharray="3 2" />
    <line x1="3" y1="15" x2="21" y2="15" strokeDasharray="3 2" />
    <line x1="9" y1="3" x2="9" y2="21" strokeDasharray="3 2" />
    <line x1="15" y1="3" x2="15" y2="21" strokeDasharray="3 2" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </Icon>
);

// Microscope - Simplified forensic scope
export const ScopeIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="10" cy="8" r="5" />
    <line x1="14" y1="12" x2="20" y2="18" />
    <line x1="6" y1="14" x2="6" y2="21" />
    <line x1="14" y1="14" x2="14" y2="21" />
    <line x1="3" y1="21" x2="17" y2="21" />
  </Icon>
);

// Layers - Stacked rectangles
export const LayersIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="4" y="14" width="16" height="6" />
    <rect x="6" y="9" width="12" height="4" fill="none" />
    <rect x="8" y="4" width="8" height="4" fill="none" />
  </Icon>
);

// Record - Circle with dot
export const RecordIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </Icon>
);

// Equilibrium - Intersecting lines
export const EquilibriumIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <line x1="4" y1="12" x2="20" y2="12" />
    <line x1="12" y1="4" x2="12" y2="20" />
    <circle cx="12" cy="12" r="4" fill="none" />
  </Icon>
);

// Heatmap - Grid with gradient squares
export const HeatmapIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="3" y="3" width="7" height="7" />
    <rect x="14" y="3" width="7" height="7" fill="none" />
    <rect x="3" y="14" width="7" height="7" fill="none" />
    <rect x="14" y="14" width="7" height="7" />
  </Icon>
);

// Playback - Play with rewind marks
export const PlaybackIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <polygon points="10,6 18,12 10,18" fill="currentColor" stroke="none" />
    <line x1="6" y1="6" x2="6" y2="18" />
  </Icon>
);

// Annotation/Palette - Marker dots
export const AnnotationIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="6" cy="6" r="3" fill="currentColor" />
    <circle cx="18" cy="6" r="3" />
    <circle cx="6" cy="18" r="3" />
    <circle cx="18" cy="18" r="3" fill="currentColor" />
    <line x1="9" y1="6" x2="15" y2="6" />
    <line x1="6" y1="9" x2="6" y2="15" />
  </Icon>
);

// Shield - Simple shield shape
export const ShieldIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M12 3 L20 7 L20 13 C20 17 16 20 12 21 C8 20 4 17 4 13 L4 7 Z" />
  </Icon>
);

// Users - Two person silhouettes
export const UsersIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="8" cy="7" r="3" />
    <path d="M3 21 L3 17 C3 15 5 14 8 14 C11 14 13 15 13 17 L13 21" />
    <circle cx="16" cy="7" r="3" />
    <path d="M14 14 C17 14 19 15 19 17 L19 21" />
  </Icon>
);

// Dashboard/Assignment - Clipboard with checkmark
export const DashboardIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="5" y="4" width="14" height="17" />
    <rect x="8" y="2" width="8" height="4" />
    <polyline points="9,12 11,14 15,10" />
  </Icon>
);

// Audit/Activity - Log list
export const AuditIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="3" y="3" width="18" height="18" />
    <line x1="7" y1="8" x2="17" y2="8" />
    <line x1="7" y1="12" x2="17" y2="12" />
    <line x1="7" y1="16" x2="13" y2="16" />
  </Icon>
);

// Synthetic/Bolt - Lightning bolt
export const SyntheticIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <polygon points="13,2 4,14 11,14 11,22 20,10 13,10" fill="currentColor" stroke="none" />
  </Icon>
);

// Target - Crosshair
export const TargetIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="3" />
    <line x1="12" y1="2" x2="12" y2="6" />
    <line x1="12" y1="18" x2="12" y2="22" />
    <line x1="2" y1="12" x2="6" y2="12" />
    <line x1="18" y1="12" x2="22" y2="12" />
  </Icon>
);

// Chart - Bar chart
export const ChartIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <line x1="4" y1="20" x2="20" y2="20" />
    <rect x="5" y="12" width="3" height="8" fill="currentColor" />
    <rect x="10" y="6" width="3" height="14" fill="currentColor" />
    <rect x="15" y="10" width="3" height="10" fill="currentColor" />
  </Icon>
);

// Eye/Analytics - Simplified eye
export const AnalyticsIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <path d="M2 12 C5 6 9 4 12 4 C15 4 19 6 22 12 C19 18 15 20 12 20 C9 20 5 18 2 12 Z" fill="none" />
    <circle cx="12" cy="12" r="3" fill="currentColor" />
  </Icon>
);

// Report/Document
export const ReportIcon = ({ size, className }) => (
  <Icon size={size} className={className}>
    <rect x="5" y="2" width="14" height="20" />
    <line x1="8" y1="7" x2="16" y2="7" />
    <line x1="8" y1="11" x2="16" y2="11" />
    <line x1="8" y1="15" x2="12" y2="15" />
  </Icon>
);

export default {
  PlayIcon,
  BalanceIcon,
  CursorIcon,
  QualityIcon,
  ScopeIcon,
  LayersIcon,
  RecordIcon,
  EquilibriumIcon,
  HeatmapIcon,
  PlaybackIcon,
  AnnotationIcon,
  ShieldIcon,
  UsersIcon,
  DashboardIcon,
  AuditIcon,
  SyntheticIcon,
  TargetIcon,
  ChartIcon,
  AnalyticsIcon,
  ReportIcon
};
